import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Space, Tabs, Typography, theme } from 'antd';
import dayjs from 'dayjs';
import type { RoyaltyReportDetailOut } from '@/api/generated/schemas';
import { StatCell } from '@/components/common/show/StatCell';
import { RoyaltyReportActions } from '@/components/settlement/RoyaltyReportActions';
import { RoyaltyReportExportButton } from '@/components/settlement/RoyaltyReportExportButton';
import { RoyaltyReportStatusTag } from '@/components/settlement/tags';
import { PublisherIconLink } from '@/components/user_catalog/publishers/PublisherIconLink';
import { formatDateTime, formatUnits } from '@/utils/format';
import { formatLedger } from '@/utils/money';
import { PayableAmount } from '@/components/settlement/PayableAmount';
import { ROYALTY_REPORT_SHOW_TABS } from '../constants';
import type { RoyaltyReportShowTab } from '../constants';
import { currentStatements, reportUnitsCount } from '../statements';

const { Text, Title } = Typography;

const T = 'settlement.royalty_reports';

const TAB_LABEL_KEYS: Record<RoyaltyReportShowTab, string> = {
  general: 'base.sections.basic_info',
  breakdown: 'settlement.royalty_reports.sections.breakdown',
};

interface RoyaltyReportShowHeaderProps {
  record: RoyaltyReportDetailOut;
  activeTab: RoyaltyReportShowTab;
  onTabChange: (key: string) => void;
  onChanged?: () => void;
}

export const RoyaltyReportShowHeader = ({
  record,
  activeTab,
  onTabChange,
  onChanged,
}: RoyaltyReportShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginXS} align="baseline" wrap>
            <Title
              level={3}
              style={{ margin: 0, fontSize: token.fontSizeHeading3 }}
            >
              {dayjs(record.period).format('YYYY-MM')}
            </Title>
            <RoyaltyReportStatusTag status={record.status} />
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`${t(`${T}.titles.show`)} · ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t(`${T}.fields.publisher_id`)}>
              <PublisherIconLink publisherId={record.publisher_id} />
            </StatCell>
            <StatCell label={t(`${T}.fields.payable_amount`)} size="lg">
              <PayableAmount
                amount={record.payable_amount}
                roundingDelta={record.rounding_delta}
                currency={record.currency}
              />
            </StatCell>
            <StatCell label={t(`${T}.fields.amount_ledger`)}>
              {formatLedger(record.amount, record.currency)}
            </StatCell>
            <StatCell label={t(`${T}.fields.units_count`)}>
              {formatUnits(reportUnitsCount(record))}
            </StatCell>
            <StatCell label={t(`${T}.fields.publishers_count`)}>
              {`${currentStatements(record.statements).length}`}
            </StatCell>
            <StatCell label={t('base.fields.created_at')}>
              {formatDateTime(record.created_at) ?? '—'}
            </StatCell>
          </Flex>
        </div>

        <Space size={4} wrap>
          {/* Renders nothing past `draft` — the component owns that rule. */}
          <RoyaltyReportActions record={record} onDone={onChanged} />
          {/* Any status; last, so it stays at the edge whatever the status. */}
          <RoyaltyReportExportButton record={record} />
        </Space>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={ROYALTY_REPORT_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
